import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term, Steps, Step } from "@/components/mt/MTBox";
import { ScaleExplorer } from "@/components/mt/ScaleExplorer";
import { ChordTree } from "@/components/mt/ChordTree";
import { NoteImage } from "@/components/mt/NoteImage";
import { NextUp } from "@/components/Callouts";

export default function MusicTheory2() {
  const { lesson, course, accent } = findLesson("music-theory", "2");
  return (
    <LessonShell
      course={course}
      courseSlug="music-theory"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/music-theory/MT2-Scale-Degrees-Animated.mp4"
        caption="Scale degrees & swaras — playing Twinkle Twinkle in numbers"
        hint="≈ 35 sec · watch first"
      />
      <Chapter number="02" eyebrow="Music Theory · Chapter Two" title="From scales to songs: numbering the notes">
        <p>
          A scale by itself is just an alphabet. To make music with it, you need a way to talk
          about <em>which</em> notes you're using and <em>in what order</em> — without tying
          yourself to a single key. The trick that has held up for centuries is also the simplest
          one imaginable: <strong>give every note in the scale a number</strong>.
        </p>
        <p>
          Once a melody is written in numbers — 1, 5, 6, 4 — you can play it in any key on the
          planet. Drop into B major, find the 1, find the 5, find the 6, find the 4, and the
          song comes out perfect. Numbers are the secret currency that lets musicians
          collaborate, transpose for singers, and pick up unfamiliar tunes by ear.
        </p>
        <MTQuote>
          Because we wrote it with numbers, you can play this song on any other scale you
          wish, right? You just have to write the scale, figure out what is the 1, the 2,
          the 3, ma, pa, dha, ni of that scale and get cracking right away.
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="The seven degrees" title="1·2·3·4·5·6·7 — and their solfège names">
        <Prose>
          <p>
            Every note in a major scale has two parallel labels: its <strong>position
            number</strong> (1 through 7) and its <strong>solfège syllable</strong> (Sa, Re, Ga,
            Ma, Pa, Dha, Ni in the Indian tradition; Do, Re, Mi, Fa, Sol, La, Ti in the Western).
            They mean the same thing — they're just two vocabularies for the same idea: <em>the
            note's place in the scale</em>.
          </p>
        </Prose>

        <Glossary>
          <Term name="1 · Sa · Do" def="The tonic — the home note. Where every melody comes to rest." />
          <Term name="2 · Re · Re" def="A connecting tone, one step above home." />
          <Term name="3 · Ga · Mi" def="The major third — the note that decides 'happy' or 'sad'." />
          <Term name="4 · Ma · Fa" def="The subdominant — away from home but stable." />
          <Term name="5 · Pa · Sol" def="The dominant — strongest pull back to 1." />
          <Term name="6 · Dha · La" def="The relative-minor anchor; warm and pensive." />
          <Term name="7 · Ni · Ti" def="The leading tone — desperately wants to resolve up to 1." />
        </Glossary>

        <NoteImage
          src="/notes/music-theory/finals/02a-scale-degrees-with-swaras.png"
          caption="Jason's note: each degree mapped to its swara (sa, re, ga, ma, pa, dha, ni) across A major, B major, C major, D major, E major, F major and G major. The numbers and the syllables stay constant — only the actual pitches under them change."
        />

        <Prose>
          <p>
            Click around the explorer below to hear the seven degrees of any major scale. Notice
            how the <em>shape</em> of the melody you build with numbers stays the same when you
            switch the tonic — only the actual pitches change. That is the whole magic of using
            numbers instead of note names.
          </p>
        </Prose>

        <ScaleExplorer tonic="A" scales={["major"]} />
      </Chapter>

      <Chapter eyebrow="Twinkle, in numbers" title="One melody, infinite keys">
        <Prose>
          <p>
            Here is a melody you almost certainly know, written entirely in scale-degree numbers:
          </p>
        </Prose>

        <TheoryBox label="Twinkle Twinkle Little Star · in numbers">
          <div className="text-center font-mt text-xl text-amber-200">
            <strong>1 1 5 5 &nbsp; 6 6 5</strong>
          </div>
          <div className="mt-1 text-center font-mt text-xl text-amber-200">
            <strong>4 4 3 3 &nbsp; 2 2 1</strong>
          </div>
          <div className="mt-2 text-center text-[13px] text-nsm-dim">
            (the same shape works in every major key — just relabel the seven notes)
          </div>
        </TheoryBox>

        <Prose>
          <p>
            Let's render that on <strong>A major</strong>. The scale is
            <span className="font-mono text-amber-300"> A B C♯ D E F♯ G♯</span>, so:
          </p>
        </Prose>

        <Steps>
          <Step><strong>1 = A.</strong> Twinkle, twinkle starts on the tonic.</Step>
          <Step><strong>5 = E.</strong> The fifth degree of A major is E.</Step>
          <Step><strong>6 = F♯.</strong> The sixth degree, just one step above the 5.</Step>
          <Step><strong>4 = D.</strong> Subdominant — the "away" anchor.</Step>
          <Step><strong>3 = C♯.</strong> The major third gives the song its bright colour.</Step>
          <Step><strong>2 = B.</strong> One step up from home.</Step>
          <Step><strong>1 = A.</strong> Final landing — the ear knows it's over.</Step>
        </Steps>

        <NoteImage
          src="/notes/music-theory/finals/02b-numbered-melodic-ideas.png"
          caption="Jason's notebook of melodic 'shapes' written in numbers. 1·7·1·2 / 1·2·3 / 5·4·3·2 / 5·5·6·5·1·7 — short phrases that can be played in any key by simply finding the matching numbers. This is how composers think when they jam."
        />

        <Prose>
          <p>
            Now move to <strong>B major</strong> and watch the magic. The scale is
            <span className="font-mono text-amber-300"> B C♯ D♯ E F♯ G♯ A♯</span>. The shape of
            the melody is identical — but every actual pitch shifts. The 5 is now F♯ (not F!).
            The 3 is now D♯. The 4 is E. Play the same number sequence and Twinkle pops out,
            transposed up a tone. That is composition <em>and</em> ear training in one move.
          </p>
        </Prose>

        <NoteImage
          src="/notes/music-theory/finals/02c-A-major-melody-mapped-to-swaras.png"
          caption="Jason's note: an A-major melody (E E F♯ E A' G♯) written under its solfège syllables (pa, pa, dha, pa, sa', ni). Once you can read this layer, you can transpose the same melody to any key in seconds."
        />
      </Chapter>

      <Chapter eyebrow="Why melodies feel finished" title="Each degree has a job">
        <Prose>
          <p>
            The seven scale degrees aren't equal in weight. Some feel like rest; others feel
            like motion that wants to resolve. This is called <em>functional harmony</em>, and
            once your ear hears it, melodies stop being mysterious — they become predictable.
          </p>
          <ul className="space-y-1 text-[14px]">
            <li><strong>1 (tonic)</strong> — home. The only note the ear truly accepts as final.</li>
            <li><strong>5 (dominant)</strong> — the strongest pull back to home.</li>
            <li><strong>4 (subdominant)</strong> — stable, but distinctly "away from home".</li>
            <li><strong>7 (leading tone)</strong> — leans up to 1 with almost magnetic force.</li>
            <li><strong>2, 6</strong> — gentle connecting tones; useful pivots.</li>
            <li><strong>3</strong> — colour. Major or minor flavour lives here.</li>
          </ul>
        </Prose>

        <TheoryBox label="The two rules of writing a melody">
          <ol className="list-decimal space-y-1 pl-5 text-[14px]">
            <li>End on the <strong>1</strong>. That is the only home the ear accepts as final.</li>
            <li>Right before the end, play the <strong>7</strong> (or the <strong>2</strong>) —
              the resolution to 1 is what makes the line feel complete.</li>
          </ol>
        </TheoryBox>

        <Prose>
          <p>
            Try it: <span className="font-mono text-amber-300">1 3 5 6 5 4 3 2 7 1</span>. The
            second-to-last note (the 7) leans up to the final 1 — and the ear breathes out. You
            just composed a melody using nothing but numbers.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Don't let the left hand sleep" title="A root drone keeps the music alive">
        <Prose>
          <p>
            When you play a number-based melody in the right hand, the left hand should not just
            sit there. The simplest, most musical thing it can do is hold a steady pulse on the
            <strong> root note</strong> of the scale (the 1, the Sa). Every melody floats more
            confidently when there's a heartbeat underneath it.
          </p>
          <p>
            On A major, that's a steady A in the bass. On B major, a steady B. The pulse can be
            quarter notes, eighth notes, a slow drone — anything that locks the music to its
            home. As the course progresses, the left hand will grow into chords, arpeggios, and
            counter-rhythms, but the root drone is the foundation everything rests on.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="A first look ahead" title="Numbers also generate chords">
        <Prose>
          <p>
            The same numbering that lets you transpose Twinkle also tells you which chords
            belong in the key. The 1, the 4, and the 5 are the bright "happy" chords. The 2, 3,
            and 6 are the moodier minor chords. The 7 is the tense one. Numbers are how
            musicians talk about chord progressions, and you'll meet that fully in Chapter 5.
            For now, just admire how the same scale tree spawns both melodies and harmonies:
          </p>
        </Prose>

        <ChordTree tonic="C" mode="major" />
      </Chapter>

      <Chapter eyebrow="Homework" title="Three melodies, twelve scales">
        <Prose>
          <p>
            Take Twinkle Twinkle, Mary Had a Little Lamb, and Happy Birthday — three melodies
            you've known since childhood. Write each one out in number form. Then play each one
            in at least three different major scales: an easy one (C, G, F), a sharp-heavy one
            (B, F♯), and a flat-heavy one (A♭, D♭). You will feel the discomfort in the keys
            you don't know yet — that discomfort is the work happening.
          </p>
          <p>
            The more scales you practise anything in, the better for your overall musicianship.
            Once these three melodies live in your hand on every key, try inventing your own
            number sequences and see what shapes feel like songs.
          </p>
        </Prose>

        <ScaleExplorer tonic="C" scales={["major"]} />
      </Chapter>

      <NextUp
        to="/lesson/music-theory-3"
        label="Chapter 3 · Minor, Pentatonic & Blues Scales"
        why="With major-scale numbering in your hands, the next move is to bend the recipe: flatten the right degrees and the major scale turns minor, pentatonic, or bluesy."
      />
    </LessonShell>
  );
}
